import type { Scene } from "@babylonjs/core/scene";
import { PointerEventTypes } from "@babylonjs/core/Events/pointerEvents";
import { KeyboardEventTypes } from "@babylonjs/core/Events/keyboardEvents";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Color3 } from "@babylonjs/core/Maths/math.color";
import type { Rig } from "./rig";
import type { Terrain } from "../render/terrain";
import { AimReticle } from "./aim";
import { SeedPouch, type Planter } from "./planting";
import { SPECIES } from "../sim/species";

/**
 * Flatscreen dev path: mouse-look (via the rig camera), click to teleport,
 * Shift+click to plant the selected species, number keys / Q-E to cycle the
 * seed pouch. Lets the scene, teleport arc/fade, and planting be exercised
 * without a headset. True XR still needs on-device verification.
 */
export interface DesktopControls {
  /** Hide the flatscreen reticles + stop handling mouse input (while in XR). */
  suspend(): void;
  resume(): void;
}

export function setupDesktopControls(
  scene: Scene,
  rig: Rig,
  terrain: Terrain,
  pouch: SeedPouch,
  planter: Planter,
): DesktopControls {
  const teleReticle = new AimReticle(scene, "tele", new Color3(0.4, 0.9, 0.5));
  const plantReticle = new AimReticle(scene, "plant", new Color3(0.9, 0.8, 0.4));
  let planting = false;
  let suspended = false;

  const hud = makeHud();
  pouch.onChange((id) => hud.setSpecies(id));

  const pickGround = (): { point: Vector3; valid: boolean } | null => {
    const hit = scene.pick(scene.pointerX, scene.pointerY, (m) => m === terrain.mesh);
    if (!hit?.pickedPoint) return null;
    const p = hit.pickedPoint;
    return { point: p, valid: terrain.isValidTarget(p.x, p.z) };
  };

  scene.onPointerObservable.add((info) => {
    if (suspended) return; // in XR: the controllers own aiming; don't leave a stray desktop reticle
    if (info.type === PointerEventTypes.POINTERMOVE) {
      const g = pickGround();
      const from = rig.camera.position;
      teleReticle.hide();
      plantReticle.hide();
      if (!g) return;
      (planting ? plantReticle : teleReticle).show(from, g.point, g.valid);
    } else if (info.type === PointerEventTypes.POINTERPICK) {
      const g = pickGround();
      if (!g || !g.valid) return;
      if (planting) planter.plant(pouch.species, g.point.x, g.point.z);
      else rig.teleport(g.point.x, g.point.z);
    }
  });

  scene.onKeyboardObservable.add((info) => {
    if (info.type !== KeyboardEventTypes.KEYDOWN) return;
    const k = info.event.key.toLowerCase();
    if (k >= "1" && k <= "4") pouch.select(Number(k) - 1);
    else if (k === "e") pouch.cycle(1);
    else if (k === "q") pouch.cycle(-1);
    else if (k === "shift") {
      planting = true;
      hud.setMode(true);
    }
  });
  scene.onKeyboardObservable.add((info) => {
    if (info.type === KeyboardEventTypes.KEYUP && info.event.key.toLowerCase() === "shift") {
      planting = false;
      hud.setMode(false);
    }
  });

  return {
    suspend: () => {
      suspended = true;
      teleReticle.hide();
      plantReticle.hide();
      hud.setHidden(true);
    },
    resume: () => {
      suspended = false;
      hud.setHidden(false);
    },
  };
}

function makeHud(): { setSpecies: (id: string) => void; setMode: (planting: boolean) => void; setHidden: (h: boolean) => void } {
  const el = document.createElement("div");
  el.style.cssText =
    "position:fixed;left:12px;bottom:12px;padding:10px 14px;border-radius:8px;" +
    "background:rgba(20,16,12,.72);color:#eee;font:13px system-ui;line-height:1.5;pointer-events:none;z-index:10";
  document.body.appendChild(el);
  let species = "grass";
  let planting = false;
  const render = (): void => {
    const [r, g, b] = SPECIES[species as keyof typeof SPECIES].color;
    const sw = `<span style="display:inline-block;width:10px;height:10px;border-radius:2px;background:rgb(${r * 255 | 0},${g * 255 | 0},${b * 255 | 0});vertical-align:-1px"></span>`;
    el.innerHTML =
      `<b>Living Garden</b> — desktop dev<br>` +
      `mode: <b style="color:${planting ? "#e9d27a" : "#7ae08a"}">${planting ? "PLANT" : "TELEPORT"}</b> (hold Shift to plant)<br>` +
      `seed: ${sw} ${species} &nbsp;(1–4 / Q·E)<br>` +
      `click ground to ${planting ? "plant" : "teleport"}`;
  };
  render();
  return {
    setSpecies: (id) => {
      species = id;
      render();
    },
    setMode: (p) => {
      planting = p;
      render();
    },
    setHidden: (h) => {
      el.style.display = h ? "none" : "";
    },
  };
}
